import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { ShieldCheck, Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Shown automatically after login whenever the current user has not accepted
 * the latest version of one or more legal documents. Blocks access until
 * they tick every checkbox and click "Accept all" — the API will then record
 * an acceptance row per (user, doc, version).
 */
export default function LegalAcceptanceModal() {
  const { user, token, getAuthHeader } = useAuth();
  const [pending, setPending] = useState([]);
  const [checked, setChecked] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token || !user) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API_URL}/api/legal/pending-acceptance`, {
          headers: getAuthHeader(),
        });
        if (cancelled) return;
        setPending(res.data.pending || []);
      } catch {
        // silent — non-blocking
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token, user?.id]);

  if (!token || !user) return null;
  // Forced password rotation takes precedence over the legal overlay.
  if (user?.must_change_password) return null;
  if (loading || pending.length === 0) return null;

  const allChecked = pending.every(p => checked[p.slug]);

  const handleAccept = async () => {
    setSubmitting(true);
    try {
      await axios.post(
        `${API_URL}/api/legal/accept`,
        { acceptances: pending.map(p => ({ slug: p.slug, version: p.version })) },
        { headers: getAuthHeader() }
      );
      setPending([]);
      // Let waiting UIs (e.g. the onboarding welcome modal) know it's safe
      // to open now that the legal overlay is gone.
      window.dispatchEvent(new CustomEvent('legal-acceptance-complete'));
    } catch (err) {
      console.error('Accept failed', err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[130] flex items-center justify-center bg-black/80 backdrop-blur-md p-4"
      role="dialog"
      aria-modal="true"
      data-testid="legal-acceptance-modal"
    >
      <div className="w-full max-w-xl bg-card border border-gold/30 rounded-sm shadow-[0_0_60px_rgba(212,175,55,0.25)] p-8">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-gold/15 flex items-center justify-center">
            <ShieldCheck className="w-5 h-5 text-gold" />
          </div>
          <h2 className="text-xl font-playfair font-bold text-foreground">
            {pending.length === 1 ? 'One update needs your attention' : `${pending.length} updates need your attention`}
          </h2>
        </div>
        <p className="text-sm text-muted-foreground mb-6">
          We've updated our legal documents. Please review and accept each to continue using WeMasterminds.
        </p>
        <div className="space-y-3 mb-6">
          {pending.map(p => (
            <label
              key={p.slug}
              className="flex items-start gap-3 p-3 border border-border rounded-sm hover:border-gold/40 transition-colors cursor-pointer"
              data-testid={`pending-doc-${p.slug}`}
            >
              <input
                type="checkbox"
                checked={!!checked[p.slug]}
                onChange={(e) => setChecked(c => ({ ...c, [p.slug]: e.target.checked }))}
                className="mt-1 w-4 h-4 accent-gold cursor-pointer"
                data-testid={`check-${p.slug}`}
              />
              <div className="flex-1">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <span className="font-semibold text-foreground">{p.title}</span>
                  <span className="text-[10px] uppercase tracking-widest text-muted-foreground">
                    v{p.version} · {p.effective_date}
                  </span>
                </div>
                <Link
                  to={`/legal/${p.slug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-gold hover:underline"
                >
                  Read the {p.title.toLowerCase()} →
                </Link>
              </div>
            </label>
          ))}
        </div>
        <Button
          onClick={handleAccept}
          disabled={!allChecked || submitting}
          className="w-full bg-gold hover:bg-gold/90 text-black font-bold uppercase tracking-widest"
          data-testid="accept-all-btn"
        >
          {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Recording…</> : 'Accept all'}
        </Button>
        <p className="text-[11px] text-muted-foreground mt-3 text-center">
          Your acceptance (with timestamp and IP) is recorded for compliance purposes.
        </p>
      </div>
    </div>
  );
}
